import { Platform } from 'react-native';
import fs from 'react-native-fs';

import { getNetworkStatus } from '../../../../hooks/useNetworkStatus';
import {
    replaceToFullPath,
    resolveImagePath
} from '../../../../services/device';
import { getMyDocumentsFromStorage } from '../../../../services/myDocuments';
import {
    getCurrentState,
    getProfileInfo,
    getVisits
} from './backupData';

// eslint-disable-next-line no-console
const toLog = (...params) => {
    // console.log(...params);
};

export const copyFromCacheToDocs = async (uri) => {
    const srcPath = uri?.split('/')
        ?.slice(0, -1)
        ?.join('/') || uri;
    const fileName = uri?.split('/')
        ?.pop();
    const docPath = `file://${fs.DocumentDirectoryPath}`;
    const cachePath = `file://${fs.CachesDirectoryPath}`;
    const isCache = Platform.OS === 'ios' ? srcPath?.endsWith('/tmp') : cachePath === srcPath;
    // console.log('copyFromCacheToDocs', {
    //     docPath,
    //     cachePath,
    //     srcPath,
    //     isCache,
    //     fs,
    // });
    if (isCache) {
        try {
            const data = await fs.readFile(uri, 'base64');
            await fs.writeFile(docPath + '/' + fileName, data, 'base64');
            await fs.unlink(uri);
            return docPath + '/' + fileName;
        } catch (e) {
            console.log('Error copying file from cache to documents:', e);
            return uri;
        }
    }
};

export const getAttachmentsList = (visits = []) => {
    const attachments = [];
    visits.forEach((visit) => {
        if (visit?.attachments && visit?.attachments?.length) {
            visit.attachments.forEach((attachment) => {
                attachments.push({
                    ...attachment,
                    visitId: visit.id,
                    checkInfo: 'na',
                });
            });
        }
    });
    return attachments;
};

export const isCachePath = (path) =>
    path?.startsWith(`file://${fs.CachesDirectoryPath}`) || path?.startsWith(fs.CachesDirectoryPath);

export const convertToDocPath = (path) => `file://${fs.DocumentDirectoryPath}/${path.split('/')
    .pop() ?? ''}`;

export const isExists = async (path) => {
    try {
        const res = await fs.exists(path);
        if (res) {
            const stat = await fs.stat(path);
            return stat?.isFile() ? stat.size ?? 0 : 0;
        }
        return 0;
    } catch (e) {
        return 0;
    }
};

export const isImageAccessible = async (attachment) => {
    const res = {
        attachment,
        result: false,
        size: 0,
        path: attachment?.path,
    };

    if (!attachment?.path) {
        return res;
    }
    let size = 0;

    try {
        size = await isExists(attachment.path);
        if (size && size > 0) {
            res.result = true;
            res.size = size;
            return res;
        }
        if (isCachePath(attachment.path)) {
            const docPath = convertToDocPath(attachment.path);
            size = await isExists(docPath);
            if (size && size > 0) {
                res.result = true;
                res.size = size;
                res.path = docPath;
                return res;
            }
        }

        const file = replaceToFullPath(resolveImagePath(attachment?.path, attachment?.path));
        size = await isExists(file);
        if (size && size > 0) {
            res.result = true;
            res.size = size;
            res.path = file;
            return res;
        }
        return res;
    } catch (e) {
        console.log('Error checking attachment accessibility:', { e });
        return res;
    }
};

export const getAttachmentsInfo = async (attachments = []) => {
    const info = {
        count: 0,
        size: 0,
        attachments: [],
    };
    const notFound = {
        count: 0,
        attachments: [],
    };
    const promises = attachments.map(isImageAccessible);
    const results = await Promise.all(promises);

    results.forEach((result) => {
        if (result.result) {
            info.count += 1;
            info.size += result.size;
            info.attachments.push({
                ...result.attachment,
                path: result.path,
                size: result.size,
            });
        } else {
            notFound.count += 1;
            notFound.attachments.push(result.attachment);
        }
    });
    return {
        info,
        notFound,
    };
};

export const getAttachmentsForBackup = async () => {
    const state = getCurrentState();
    const {
        profileId,
        hashKey,
    } = getProfileInfo();
    const { prefs } = state ?? {};
    const { data = [] } = prefs ?? {};
    const network = await getNetworkStatus();
    const {
        backupAttachmentsWifi = true,
        backupVisitAttachments = true,
        backupMyDocs = true,
    } = data ?? {};

    if (!backupVisitAttachments && !backupMyDocs) {
        return [];
    }

    if (backupAttachmentsWifi && !network?.isWifi) {
        toLog('Skipping backup due to non-WiFi connection:', network);
        return [];
    }

    const visits = await getVisits();
    let myDocs = [];
    if (backupMyDocs) {
        myDocs = await getMyDocumentsFromStorage({
            profileId,
            hashKey,
        }) ?? [];
        toLog('My documents for backup:', myDocs);
    }

    const attachmentList = [ ...((backupVisitAttachments ? getAttachmentsList(visits) : []) ?? []), ...myDocs ];
    toLog('Attachments list for backup:', {
        attachmentList,
        myDocs,
    });
    if (!attachmentList || !attachmentList.length) {
        toLog('No attachments found for backup');
        return [];
    }
    const { info } = await getAttachmentsInfo(attachmentList);
    toLog('Attachments info for backup with info:', info);

    return info.attachments || [];
};
